/**
 * Reads back the JSON filter shapes the per-shape matchers in
 * `predicateJsonBranch.ts` / `predicateJsonBranchPg.ts` do not cover — the
 * lateral / subquery forms `buildJsonConditionSql` (`jsonFilterSql.ts`) writes:
 *
 *  - `any_*` / `all_*` (incl. lookup- and enum-in-array) over a `[*]` path —
 *    `EXISTS (SELECT 1 FROM <lateral> WHERE …)` on PostgreSQL / SQL Server /
 *    Oracle / SQLite, `(SELECT COUNT(*) FROM JSON_TABLE(…) WHERE …) > 0` on MySQL;
 *  - `path_eq` / `path_neq` / `path_contains` / `has_property` on a wildcard or
 *    recursive (`$..key`) path;
 *  - SQL Server's `has_property` on a non-root path (`OPENJSON` key probe);
 *  - `is_empty` / `is_not_empty` on every engine;
 *  - PostgreSQL 19's case-insensitive matches (`path_ieq` / `any_ieq`).
 *
 * Instead of one hand-written regex per shape and engine, it proposes
 * conditions from the predicate's own pieces — the column reference (the first
 * identifier that is not a function name or a leading keyword), the text and
 * number literals, `IN (…)` lists, and paths rebuilt from those literals the
 * way the emitter split them (row path + tail path, parent + key, `$.**.` for
 * `..`) — and accepts one only when re-emitting it with the shared emitter
 * gives back the predicate byte for byte. Anything else stays `null`, so the
 * pill falls back to raw text.
 *
 * Where two conditions write the same SQL, the one the chip itself would show
 * wins: an `any_*` operator for a `[*]` path (the chip's sub-operator list
 * offers the aggregates there), the scalar operator otherwise; a scalar
 * `*_enum` / `*_lookup` pick reads back as `*_eq` and is upgraded from the
 * column's MetaInfo by `upgradeReopenedCondition`.
 */

import type { DatabaseEngineId } from '../../../../core/engines/engineTypes';
import type { FilterCondition, JsonSubOperator } from '../../core/types/workspaceTypes';
import { buildConditionSql } from './filterWhereBuilder';
import { hasJsonPathWildcard, validityGuardSql } from './jsonFilterSql';
import { QUALIFIED_IDENT_SRC } from './predicateIdentGrammar';
import { pgArrayLiteralToPath } from './predicateJsonBranchPg';
import {
  decodeTextLiteral,
  MSSQL_COLLATE_SUFFIX_SRC,
  splitTopLevelCommaList,
  type JsonParseResult,
} from './predicateJsonHelpers';

type Value = string | number | string[];

/** Keywords that can precede the column reference in an emitted JSON shape. */
const LEAD_KEYWORDS = new Set(['NOT', 'EXISTS', 'SELECT', 'FROM', 'CASE', 'WHEN']);

const PATH_OPS: readonly JsonSubOperator[] = ['has_property', 'not_has_property', 'path_is_null', 'path_is_not_null'];
const SCALAR_VALUE_OPS: readonly JsonSubOperator[] = [
  'path_eq', 'path_neq', 'path_contains', 'path_ieq', 'path_gt', 'path_gte', 'path_lt', 'path_lte',
];
const AGGREGATE_VALUE_OPS: readonly JsonSubOperator[] = [
  'any_eq', 'any_contains', 'any_ieq', 'any_gt', 'any_gte', 'any_lt', 'any_lte',
  'all_eq', 'all_gt', 'all_gte', 'all_lt', 'all_lte',
];
const LIST_OPS: readonly JsonSubOperator[] = ['property_enum', 'any_enum', 'all_enum'];

/** PG 19's `<path> ? (@.lower() == "<value>")` jsonpath (`jsonpathLowerEqLiteral`). */
const IEQ_JSONPATH_RE = /^([\s\S]*) \? \(@\.lower\(\) == "((?:[^"\\]|\\.)*)"\)$/;

type Pieces = {
  readonly field: string;
  /** Decoded text literals, in order. */
  readonly texts: readonly string[];
  readonly numbers: readonly number[];
  /** Decoded `IN (…)` lists. */
  readonly lists: readonly string[][];
  readonly blanked: string;
};

/** Split `s` into its literals and the text around them (literals blanked out). */
function scan(s: string, engine: DatabaseEngineId): Pieces | null {
  const texts: string[] = [];
  let blanked = '';
  let last = 0;
  // SQL Server's `N'…'` (and the `COLLATE` of a value outside the BMP) counts
  // as the literal, so its `N` is never taken for the column.
  const literalRe = engine === 'mssql'
    ? new RegExp(`(?<![\\w$#@])N'(?:[^']|'')*'(?:${MSSQL_COLLATE_SUFFIX_SRC})?|'(?:[^']|'')*'`, 'g')
    : /_utf8mb4\s*[xX]'[0-9A-Fa-f]*'|'(?:[^']|'')*'/g;
  for (const m of s.matchAll(literalRe)) {
    const decoded = decodeTextLiteral(m[0], engine);
    if (decoded === null) return null;
    texts.push(decoded);
    blanked += s.slice(last, m.index) + ' '.repeat(m[0].length);
    last = m.index + m[0].length;
  }
  blanked += s.slice(last);

  let field: string | null = null;
  for (const m of blanked.matchAll(new RegExp(QUALIFIED_IDENT_SRC, 'g'))) {
    if (/(?:\.|::|[\w$#@])$/.test(blanked.slice(0, m.index))) continue;
    if (/^\s*\(/.test(blanked.slice(m.index + m[0].length))) continue;
    if (LEAD_KEYWORDS.has(m[0].toUpperCase())) continue;
    field = m[0];
    break;
  }
  if (field === null) return null;

  const numbers = [...blanked.matchAll(/(?<![\w.$#@"`\]])-?\d+(?:\.\d+)?(?![\w.])/g)].map((m) => Number(m[0]));

  const lists: string[][] = [];
  for (const m of blanked.matchAll(/\bIN \(/g)) {
    const open = m.index + m[0].length - 1;
    let depth = 0;
    let close = -1;
    for (let i = open; i < blanked.length && close < 0; i++) {
      if (blanked[i] === '(') depth += 1;
      else if (blanked[i] === ')' && --depth === 0) close = i;
    }
    const parts = close > 0 ? splitTopLevelCommaList(s.slice(open + 1, close)) : null;
    const values = parts?.map((p) => decodeTextLiteral(p, engine));
    if (values && values.every((v): v is string => v !== null)) lists.push(values);
  }
  return { field, texts, numbers, lists, blanked };
}

/** JSONPath candidates, rebuilt from the literals the way the emitter split them. */
function candidatePaths(texts: readonly string[], engine: DatabaseEngineId): string[] {
  const out = new Set<string>();
  const paths = texts.filter((t) => t.startsWith('$'));
  // Row path + tail path (JSON_TABLE / OPENJSON / json_each laterals): the
  // wildcard forms first, so `$.tags` + `j.[value]` reads as `$.tags[*]`.
  for (let i = 0; i < paths.length; i++) {
    for (let j = i + 1; j < paths.length; j++) {
      if (paths[i]!.endsWith('[*]')) out.add(`${paths[i]}${paths[j]!.slice(1)}`);
      out.add(`${paths[i]}[*]${paths[j]!.slice(1)}`);
    }
    out.add(`${paths[i]}[*]`);
  }
  for (const t of texts) {
    const ieq = t.match(IEQ_JSONPATH_RE);
    if (ieq) out.add(ieq[1]!);
    if (t.includes('.**.')) out.add(t.replace(/\.\*\*\./g, '..'));
    if (t.startsWith('$')) out.add(t);
    if (engine === 'pgsql' && t.startsWith('{')) {
      const p = pgArrayLiteralToPath(t);
      if (p) out.add(p);
    }
  }
  // SQL Server's key probe: `OPENJSON(col, '<parent>') WHERE [key] = '<key>'`.
  for (let i = 0; i + 1 < texts.length; i++) {
    const [parent, key] = [texts[i]!, texts[i + 1]!];
    if (parent.startsWith('$') && !key.startsWith('$')) {
      // A key the path has to quote (`$.a."東京"`) reads back quoted.
      if (!/^[A-Za-z_$][\w$]*$/.test(key)) out.add(`${parent}."${key.replace(/["\\]/g, '\\$&')}"`);
      out.add(`${parent}.${key}`);
      out.add(`${parent}[${key}]`);
    }
  }
  return [...out];
}

/** Comparison-value candidates: PG 19 lowered values, LIKE bodies, texts, numbers. */
function candidateValues(texts: readonly string[], numbers: readonly number[]): Array<string | number> {
  const out = new Set<string | number>();
  for (const t of texts) {
    const ieq = t.match(IEQ_JSONPATH_RE);
    if (ieq) out.add(ieq[2]!.replace(/\\(.)/g, '$1'));
    // A contains pattern (`containsLikeSql`, `!`-escaped), or one saved with the earlier `\` escape.
    if (t.length >= 2 && t.startsWith('%') && t.endsWith('%')) {
      out.add(t.slice(1, -1).replace(/!(.)/g, '$1'));
      out.add(t.slice(1, -1).replace(/\\([\\%_])/g, '$1'));
    }
  }
  for (const t of texts) out.add(t);
  for (const n of numbers) out.add(n);
  return [...out];
}

/**
 * The `json` condition whose emit is exactly `s`, or `null`. See the module
 * header for how the candidates are built and which one wins a tie.
 */
export function parseJsonPredicateByEmit(s: string, engine: DatabaseEngineId): JsonParseResult | null {
  if (engine === 'mock' || engine === 'kusto' || !/json/i.test(s)) return null;
  const pieces = scan(s, engine);
  if (!pieces) return null;
  const { field, texts, numbers, lists, blanked } = pieces;
  const guard = engine === 'pgsql' ? null : validityGuardSql(engine, field);
  const declared =
    engine === 'pgsql'
      ? s.includes(`(CASE WHEN ${field} IS JSON THEN (${field})::jsonb END)`)
      : s.startsWith(`${guard} AND `);

  const matches = (sub: JsonSubOperator, path?: string, value?: Value): JsonParseResult | null => {
    const condition: FilterCondition = {
      operator: 'json',
      jsonSubOperator: sub,
      ...(path !== undefined ? { path } : {}),
      ...(value !== undefined ? { value } : {}),
      jsonDeclaredOnText: declared,
    };
    try {
      return buildConditionSql(field, condition, engine) === s ? { field, condition } : null;
    } catch {
      return null; // an engine that cannot write this shape (e.g. `..` on MySQL)
    }
  };

  for (const sub of ['is_empty', 'is_not_empty'] as const) {
    const hit = matches(sub);
    if (hit) return hit;
  }
  const values = candidateValues(texts, numbers);
  const hasLike = blanked.includes(' LIKE ');
  for (const path of candidatePaths(texts, engine)) {
    for (const sub of PATH_OPS) {
      const hit = matches(sub, path);
      if (hit) return hit;
    }
    const valueOps = hasJsonPathWildcard(path)
      ? [...AGGREGATE_VALUE_OPS, ...SCALAR_VALUE_OPS]
      : [...SCALAR_VALUE_OPS, ...AGGREGATE_VALUE_OPS];
    for (const sub of valueOps) {
      if (sub.endsWith('_contains') !== hasLike) continue;
      for (const value of values) {
        const hit = matches(sub, path, value);
        if (hit) return hit;
      }
    }
    for (const sub of LIST_OPS) {
      for (const list of lists) {
        const hit = matches(sub, path, list);
        if (hit) return hit;
      }
    }
  }
  return null;
}
